/**
 * Privacy Consent Block (US Opt-Out)
 *
 * Injection block that surfaces a CCPA/CPRA-style "Do Not Sell or Share My
 * Personal Information" mechanism on every page of a site. Modeled on the
 * US opt-out regime (California, Colorado, Texas, Virginia, Connecticut,
 * Oregon, …): trackers load by default; this block lets a visitor opt out,
 * and honors the Global Privacy Control (GPC) browser signal automatically.
 *
 * UI (fixed-position, portaled to <body> to escape any theme layout
 * ancestor that would break `position: fixed`):
 *
 *   - First-visit banner — full-width bottom bar with a short notice, a
 *     privacy policy link, an "OK" acknowledge button, and a "Your Privacy
 *     Choices" button that opens the preferences panel. Clicking "OK"
 *     hides it permanently on regular pages. On privacy pages (URL path
 *     containing "privacy") the banner is always shown — it is the
 *     permanent opt-out mechanism. Hiding the mechanism elsewhere is
 *     permitted by CPRA § 1798.135(b): a business that honors the GPC
 *     opt-out signal frictionlessly (this block does, unconditionally) is
 *     not required to post an always-visible opt-out link, provided the
 *     privacy policy describes the opt-out right and GPC honoring.
 *   - Preferences panel — modal opened from the banner where the visitor
 *     opts out or back in.
 *
 * Opt-out state and GPC detection live in `../../shared/consent.ts`. The
 * `useTrackingAllowed()` hook in `../../shared/analytics.ts` composes with
 * that state so gated tracker blocks unmount their scripts / unsubscribe
 * their event forwarders reactively when the visitor opts out.
 */

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import type { BlockDefinition, BlockProps } from '../../types';
import type {
  ComponentContentSchema,
  ComponentDesignSchema,
} from '@vibesites/runtime';
import {
  useBlockToken,
  EditableBlockToken,
  IS_IN_EDITOR_FRAME,
} from '@vibesites/runtime/react';
import {
  getConsentState,
  subscribeConsent,
  setOptOut,
  setCategoriesGated,
  type TrackingCategory,
} from '../../shared/consent';

const BANNER_DISMISSED_STORAGE_KEY = 'vibesites:privacy:banner_dismissed';

// Fallbacks for token values. These mirror the schema defaults so the block
// still functions if a site somehow ships without them (e.g., during migration).
const DEFAULT_OPT_OUT_SCOPE: 'all' | 'advertising' = 'advertising';
const DEFAULT_BANNER_TEXT =
  'We use cookies and similar technologies to improve your experience, analyze site usage, and deliver relevant advertising. California residents and others may opt out of the sale or sharing of their personal information.';
const DEFAULT_ACCEPT_LABEL = 'OK';
// CPPA regs explicitly allow "Your Privacy Choices" as the opt-out link text
// (Cal. Code Regs. tit. 11, § 7015) — shorter and quieter than the full
// "Do Not Sell or Share My Personal Information" phrase.
const DEFAULT_OPT_OUT_LABEL = 'Your Privacy Choices';
// The preferences panel row names the specific statutory right, independent
// of the (customizable) link label.
const OPT_OUT_RIGHT_LABEL = 'Do Not Sell or Share My Personal Information';
const DEFAULT_PRIVACY_POLICY_LABEL = 'Privacy Policy';
const DEFAULT_PANEL_HEADING = 'Your Privacy Choices';

// ---------------------------------------------------------------------------
// Persistence helpers (banner dismissal only — opt-out itself lives in the
// consent store).
// ---------------------------------------------------------------------------

function readBannerDismissed(): boolean {
  if (IS_IN_EDITOR_FRAME) return false;
  try {
    return window.localStorage?.getItem(BANNER_DISMISSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeBannerDismissed(): void {
  if (IS_IN_EDITOR_FRAME) return;
  try {
    window.localStorage?.setItem(BANNER_DISMISSED_STORAGE_KEY, '1');
  } catch {
    // Fine — the banner reappears next visit, and remains on privacy pages.
  }
}

/**
 * Current pathname, updated on history navigation. Injection blocks remount
 * on SPA page changes, but popstate is tracked too so browser back/forward
 * within a long-lived mount stays accurate.
 */
function usePathname(): string {
  const [pathname, setPathname] = useState<string>(() =>
    typeof window === 'undefined' ? '' : window.location.pathname,
  );

  useEffect(() => {
    const update = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);

  return pathname;
}

// ---------------------------------------------------------------------------
// UI subcomponents
// ---------------------------------------------------------------------------

interface Palette {
  optOutScope: 'all' | 'advertising';
  bannerText: string;
  acceptLabel: string;
  optOutLabel: string;
  privacyPolicyUrl: string;
  privacyPolicyLabel: string;
  panelHeading: string;
}

const BASE_Z_INDEX = 2147483000;

function Banner({
  palette,
  onAccept,
  onOptOut,
  onOpenPreferences,
}: {
  palette: Palette;
  onAccept: () => void;
  onOptOut: () => void;
  onOpenPreferences: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-label="Privacy notice"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: BASE_Z_INDEX,
        padding: '16px 20px',
        background: 'var(--vibe-color-surface, #ffffff)',
        color: 'var(--vibe-color-text, #111827)',
        borderTop: '1px solid var(--vibe-color-border, #e5e7eb)',
        boxShadow: 'var(--vibe-shadow-lg, 0 -6px 24px rgba(0, 0, 0, 0.12))',
        fontFamily: 'var(--vibe-font-body, system-ui, sans-serif)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}
    >
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <EditableBlockToken
          localName="banner_text"
          type="richtext"
          label="Banner text"
          as="p"
          style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}
        >
          {palette.bannerText}
        </EditableBlockToken>
        {palette.privacyPolicyUrl ? (
          <a
            href={palette.privacyPolicyUrl}
            style={{
              fontSize: 13,
              marginTop: 6,
              display: 'inline-block',
              color: 'var(--vibe-color-primary, #2563eb)',
              textDecoration: 'underline',
            }}
          >
            {palette.privacyPolicyLabel}
          </a>
        ) : null}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onOpenPreferences}
          style={{
            fontFamily: 'inherit',
            fontSize: 13,
            padding: '8px 14px',
            borderRadius: 'var(--vibe-radius-md, 6px)',
            border: '1px solid var(--vibe-color-border, #d1d5db)',
            background: 'transparent',
            color: 'var(--vibe-color-text, #111827)',
            cursor: 'pointer',
          }}
        >
          {palette.optOutLabel}
        </button>
        <button
          type="button"
          onClick={onAccept}
          style={{
            fontFamily: 'inherit',
            fontSize: 13,
            padding: '8px 16px',
            borderRadius: 'var(--vibe-radius-md, 6px)',
            border: 'none',
            background: 'var(--vibe-color-primary, #2563eb)',
            color: 'var(--vibe-color-text-on-primary, #ffffff)',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          {palette.acceptLabel}
        </button>
      </div>
      {/* Hidden reference so a direct opt-out from the banner works too. */}
      <button
        type="button"
        onClick={onOptOut}
        style={{ display: 'none' }}
        aria-hidden
      />
    </div>
  );
}

function PreferencesPanel({
  palette,
  optedOut,
  source,
  onOptIn,
  onOptOut,
  onClose,
}: {
  palette: Palette;
  optedOut: boolean;
  source: 'user' | 'gpc' | null;
  onOptIn: () => void;
  onOptOut: () => void;
  onClose: () => void;
}) {
  const gpcActive = source === 'gpc';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="vibesites-privacy-panel-heading"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: BASE_Z_INDEX + 1,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        fontFamily: 'var(--vibe-font-body, system-ui, sans-serif)',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 480,
          width: '100%',
          background: 'var(--vibe-color-surface, #ffffff)',
          color: 'var(--vibe-color-text, #111827)',
          borderRadius: 'var(--vibe-radius-md, 8px)',
          boxShadow: 'var(--vibe-shadow-lg, 0 20px 60px rgba(0, 0, 0, 0.25))',
          padding: 24,
        }}
      >
        <h2
          id="vibesites-privacy-panel-heading"
          style={{
            margin: '0 0 12px',
            fontSize: 18,
            fontWeight: 700,
            fontFamily: 'var(--vibe-font-heading, inherit)',
          }}
        >
          {palette.panelHeading}
        </h2>
        <p
          style={{
            margin: '0 0 16px',
            fontSize: 14,
            lineHeight: 1.5,
            color: 'var(--vibe-color-text-muted, #4b5563)',
          }}
        >
          Under California and other US state privacy laws, you may direct us
          not to sell or share your personal information with third parties
          for cross-context behavioral advertising. Your choice is stored on
          this device.
        </p>

        <div
          style={{
            border: '1px solid var(--vibe-color-border, #e5e7eb)',
            borderRadius: 'var(--vibe-radius-md, 6px)',
            padding: 16,
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {OPT_OUT_RIGHT_LABEL}
              </div>
              <div
                style={{
                  fontSize: 12,
                  marginTop: 4,
                  color: 'var(--vibe-color-text-muted, #6b7280)',
                }}
              >
                {optedOut
                  ? gpcActive
                    ? 'Opted out — set by your browser (Global Privacy Control).'
                    : 'You have opted out on this device.'
                  : 'Advertising and, if configured, analytics trackers may share information.'}
              </div>
            </div>
            <div>
              {optedOut ? (
                <button
                  type="button"
                  onClick={onOptIn}
                  disabled={gpcActive}
                  title={
                    gpcActive
                      ? 'Your browser is sending a Global Privacy Control signal, which we honor automatically.'
                      : undefined
                  }
                  style={{
                    fontFamily: 'inherit',
                    fontSize: 13,
                    padding: '6px 12px',
                    borderRadius: 'var(--vibe-radius-md, 6px)',
                    border: '1px solid var(--vibe-color-border, #d1d5db)',
                    background: 'transparent',
                    color: 'var(--vibe-color-text, #111827)',
                    cursor: gpcActive ? 'not-allowed' : 'pointer',
                    opacity: gpcActive ? 0.6 : 1,
                  }}
                >
                  Opt back in
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onOptOut}
                  style={{
                    fontFamily: 'inherit',
                    fontSize: 13,
                    padding: '6px 12px',
                    borderRadius: 'var(--vibe-radius-md, 6px)',
                    border: 'none',
                    background: 'var(--vibe-color-primary, #2563eb)',
                    color: 'var(--vibe-color-text-on-primary, #ffffff)',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Opt out
                </button>
              )}
            </div>
          </div>
        </div>

        {palette.privacyPolicyUrl ? (
          <a
            href={palette.privacyPolicyUrl}
            style={{
              fontSize: 13,
              color: 'var(--vibe-color-primary, #2563eb)',
              textDecoration: 'underline',
            }}
          >
            {palette.privacyPolicyLabel}
          </a>
        ) : null}

        <div style={{ textAlign: 'right', marginTop: 20 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              fontFamily: 'inherit',
              fontSize: 13,
              padding: '8px 16px',
              borderRadius: 'var(--vibe-radius-md, 6px)',
              border: '1px solid var(--vibe-color-border, #d1d5db)',
              background: 'transparent',
              color: 'var(--vibe-color-text, #111827)',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Block component
// ---------------------------------------------------------------------------

function PrivacyConsentComponent(_props: BlockProps) {
  // useBlockToken returns `string | null`; coerce to strings with our
  // documented defaults so the Palette type below stays clean.
  const optOutScopeToken =
    useBlockToken('opt_out_scope', { defaultValue: DEFAULT_OPT_OUT_SCOPE }) ??
    DEFAULT_OPT_OUT_SCOPE;
  const bannerText =
    useBlockToken('banner_text', { defaultValue: DEFAULT_BANNER_TEXT }) ??
    DEFAULT_BANNER_TEXT;
  const acceptLabel =
    useBlockToken('accept_button_label', { defaultValue: DEFAULT_ACCEPT_LABEL }) ??
    DEFAULT_ACCEPT_LABEL;
  const optOutLabel =
    useBlockToken('opt_out_link_label', { defaultValue: DEFAULT_OPT_OUT_LABEL }) ??
    DEFAULT_OPT_OUT_LABEL;
  const privacyPolicyUrl =
    useBlockToken('privacy_policy_url', { defaultValue: '' }) ?? '';
  const privacyPolicyLabel =
    useBlockToken('privacy_policy_label', {
      defaultValue: DEFAULT_PRIVACY_POLICY_LABEL,
    }) ?? DEFAULT_PRIVACY_POLICY_LABEL;
  const panelHeading =
    useBlockToken('panel_heading', { defaultValue: DEFAULT_PANEL_HEADING }) ??
    DEFAULT_PANEL_HEADING;

  const optOutScope: 'all' | 'advertising' =
    optOutScopeToken === 'all' ? 'all' : DEFAULT_OPT_OUT_SCOPE;

  // Push the configured scope into the consent store so the shared gating
  // hook knows which categories the opt-out applies to. Running as an
  // effect (not during render) keeps the store call safe under StrictMode.
  useEffect(() => {
    const categories: TrackingCategory[] =
      optOutScope === 'all' ? ['advertising', 'analytics'] : ['advertising'];
    setCategoriesGated(categories);
  }, [optOutScope]);

  const consent = useSyncExternalStore(
    subscribeConsent,
    getConsentState,
    getConsentState,
  );

  const [bannerDismissed, setBannerDismissed] = useState<boolean>(() =>
    readBannerDismissed(),
  );
  const [panelOpen, setPanelOpen] = useState(false);

  // The banner is the block's only opt-out entry point:
  // - Regular pages: shown until dismissed (or the visitor is already opted
  //   out from a prior visit / GPC — nothing left to ask). In the editor
  //   iframe the consent check is skipped (the editor user's own browser
  //   state shouldn't hide the banner they're editing); dismissal there is
  //   session-only, so a reload brings it back.
  // - Privacy pages (URL path contains "privacy"): always shown, permanently.
  //   This keeps an interactive opt-out mechanism reachable after dismissal;
  //   hiding it elsewhere is permitted because GPC is honored frictionlessly
  //   (CPRA § 1798.135(b) alternative — the privacy policy must describe it).
  const pathname = usePathname();
  const isPrivacyPage = pathname.toLowerCase().includes('privacy');
  const bannerVisible =
    isPrivacyPage ||
    (!bannerDismissed && (IS_IN_EDITOR_FRAME || !consent.optedOut));

  const palette: Palette = useMemo(
    () => ({
      optOutScope,
      bannerText,
      acceptLabel,
      optOutLabel,
      privacyPolicyUrl,
      privacyPolicyLabel,
      panelHeading,
    }),
    [
      optOutScope,
      bannerText,
      acceptLabel,
      optOutLabel,
      privacyPolicyUrl,
      privacyPolicyLabel,
      panelHeading,
    ],
  );

  // In the editor iframe, dismissal and opt-out are session-only: React
  // state updates so the editor can preview the interaction, but nothing is
  // persisted and the consent store is untouched (an editor's clicks must
  // not disable trackers or write cookies in their own browser). A reload
  // brings the banner back.
  const handleAccept = () => {
    setBannerDismissed(true);
    if (IS_IN_EDITOR_FRAME) return;
    writeBannerDismissed();
  };

  const handleOptOut = () => {
    setBannerDismissed(true);
    setPanelOpen(false);
    if (IS_IN_EDITOR_FRAME) return;
    setOptOut(true);
    writeBannerDismissed();
  };

  const handleOptIn = () => {
    if (IS_IN_EDITOR_FRAME) return;
    setOptOut(false);
  };

  const handleOpenPreferences = () => setPanelOpen(true);
  const handleClosePreferences = () => setPanelOpen(false);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      {bannerVisible && (
        <Banner
          palette={palette}
          onAccept={handleAccept}
          onOptOut={handleOptOut}
          onOpenPreferences={handleOpenPreferences}
        />
      )}
      {panelOpen && (
        <PreferencesPanel
          palette={palette}
          optedOut={consent.optedOut}
          source={consent.source}
          onOptIn={handleOptIn}
          onOptOut={handleOptOut}
          onClose={handleClosePreferences}
        />
      )}
    </>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Content schema
// ---------------------------------------------------------------------------

const contentSchema: ComponentContentSchema = {
  slot: 'PrivacyConsent',
  displayName: 'Privacy Consent (US Opt-Out)',
  tokens: {
    opt_out_scope: {
      label: 'Opt-out Scope',
      type: 'text',
      defaultValue: DEFAULT_OPT_OUT_SCOPE,
      description:
        'One of: "advertising" (default; opt-out disables only ad/sharing trackers — Meta, Google Ads, GTM, AdTaxi, Basis, OpenAI, image pixels, hy.ly) or "all" (also disables first-party analytics — GA4, Clarity, CallRail).',
    },
    banner_text: {
      label: 'Banner Text',
      type: 'richtext',
      defaultValue: DEFAULT_BANNER_TEXT,
      description:
        'Short notice shown in the banner (first visit on regular pages; always on privacy pages).',
    },
    accept_button_label: {
      label: 'Accept Button Label',
      type: 'text',
      defaultValue: DEFAULT_ACCEPT_LABEL,
      description: 'Label for the button that dismisses the banner without opting out.',
    },
    opt_out_link_label: {
      label: 'Opt-out Button Label',
      type: 'text',
      defaultValue: DEFAULT_OPT_OUT_LABEL,
      description:
        'Label for the banner button that opens the privacy preferences panel. CCPA requires "Your Privacy Choices" (default) or "Do Not Sell or Share My Personal Information"; changing this text to anything else may affect compliance.',
    },
    privacy_policy_url: {
      label: 'Privacy Policy URL',
      type: 'url',
      defaultValue: '',
      description: 'Optional link shown in the banner and preferences panel.',
    },
    privacy_policy_label: {
      label: 'Privacy Policy Link Label',
      type: 'text',
      defaultValue: DEFAULT_PRIVACY_POLICY_LABEL,
      description: 'Text for the privacy policy link. Ignored when the URL is empty.',
    },
    panel_heading: {
      label: 'Preferences Panel Heading',
      type: 'text',
      defaultValue: DEFAULT_PANEL_HEADING,
      description: 'Heading shown at the top of the preferences panel.',
    },
  },
};

// ---------------------------------------------------------------------------
// Design schema
// ---------------------------------------------------------------------------

const designSchema: ComponentDesignSchema = {
  slot: 'PrivacyConsent',
  displayName: 'Privacy Consent',
  tokens: {
    'color.surface': {
      label: 'Surface Background',
      type: 'color',
      cssVariable: '--vibe-color-surface',
      description: 'Background color for the banner and preferences panel.',
    },
    'color.text': {
      label: 'Text Color',
      type: 'color',
      cssVariable: '--vibe-color-text',
      description: 'Primary text color.',
    },
    'color.textMuted': {
      label: 'Muted Text',
      type: 'color',
      cssVariable: '--vibe-color-text-muted',
      description: 'Secondary description text color.',
    },
    'color.primary': {
      label: 'Primary Color',
      type: 'color',
      cssVariable: '--vibe-color-primary',
      description: 'Accept button background and privacy policy link color.',
    },
    'color.textOnPrimary': {
      label: 'Text on Primary',
      type: 'color',
      cssVariable: '--vibe-color-text-on-primary',
      description: 'Text color on the primary-colored accept button.',
    },
    'color.border': {
      label: 'Border Color',
      type: 'color',
      cssVariable: '--vibe-color-border',
      description: 'Border color for the banner, buttons, and panel.',
    },
    'radius.md': {
      label: 'Corner Radius',
      type: 'radius',
      cssVariable: '--vibe-radius-md',
      description: 'Corner rounding for buttons and the preferences panel.',
    },
    'shadow.lg': {
      label: 'Elevation Shadow',
      type: 'shadow',
      cssVariable: '--vibe-shadow-lg',
      description: 'Elevation shadow above the banner and behind the preferences panel.',
    },
    'font.body': {
      label: 'Body Font',
      type: 'font',
      cssVariable: '--vibe-font-body',
      description: 'Font family used throughout the consent UI.',
    },
    'font.heading': {
      label: 'Heading Font',
      type: 'font',
      cssVariable: '--vibe-font-heading',
      description: 'Font family for the preferences panel heading.',
    },
  },
};

// ---------------------------------------------------------------------------
// Block definition
// ---------------------------------------------------------------------------

export const privacyConsentBlock: BlockDefinition = {
  id: 'privacy-consent',
  name: 'Privacy Consent (US Opt-Out)',
  description:
    'CCPA/CPRA-style privacy consent banner: dismissible on regular pages, always shown on privacy pages as the permanent opt-out mechanism. Honors the Global Privacy Control browser signal automatically. Gates advertising trackers (and optionally analytics) via the shared consent store.',
  category: 'Analytics',
  placement: 'injection',
  defaultScope: 'global',
  component: PrivacyConsentComponent,
  contentSchema,
  designSchema,
  requirements: {
    designTokens: [
      'color.surface',
      'color.text',
      'color.textMuted',
      'color.primary',
      'color.textOnPrimary',
      'color.border',
      'radius.md',
      'shadow.lg',
      'font.body',
      'font.heading',
    ],
    dataTokens: [],
  },
  tags: [
    'privacy',
    'consent',
    'cookie',
    'ccpa',
    'cpra',
    'gpc',
    'opt-out',
    'do-not-sell',
    'compliance',
  ],
};
